import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Heart, MessageCircle, Send, Plus, Trash2, MapPin, CheckCircle2, Users, Paperclip, FileText, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { getInitials, timeAgo } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { SpotifyLinkCard, SPOTIFY_BIBLE_TALKS } from '../components/SpotifyLinkCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  general: 'bg-slate-100 text-slate-700',
  testimony: 'bg-church-coral/10 text-church-coral',
  announcement: 'bg-church-teal/10 text-church-teal',
  prayer: 'bg-purple-100 text-purple-700',
  sermon_notes: 'bg-amber-100 text-amber-700',
  social: 'bg-emerald-100 text-emerald-700',
};

const categoryLabels = {
  general: 'General',
  testimony: 'Testimony',
  announcement: 'Announcement',
  prayer: 'Prayer',
  sermon_notes: 'Sermon Notes',
  social: 'Social',
};

const PostCard = ({ post, currentUserId, onLike, onComment, onDelete }) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isLiked = post.likes?.includes(currentUserId);
  const isOwner = post.author_id === currentUserId;

  const handleComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await onComment(post.id, newComment);
      setNewComment('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="post-card" data-testid={`post-card-${post.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={post.author_image} alt={post.author_name} />
              <AvatarFallback className="bg-church-teal text-white">
                {getInitials(post.author_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-slate-800">{post.author_name}</p>
              <p className="text-sm text-muted-foreground">{timeAgo(post.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[post.category] || categoryColors.general}`}>
              {categoryLabels[post.category] || post.category}
            </span>
            {isOwner && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-400 hover:text-red-500"
                onClick={() => onDelete(post.id)}
                data-testid={`delete-post-${post.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-slate-700 whitespace-pre-wrap">{post.content}</p>
        {(post.files || []).length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid={`post-files-${post.id}`}>
            {post.files.map((f) => {
              const isImg = (f.content_type || '').startsWith('image/');
              const isPdf = (f.content_type || '').includes('pdf');
              const isPptx = (f.content_type || '').includes('presentation');
              const emoji = isImg ? '🖼' : isPdf ? '📄' : isPptx ? '📊' : '📎';
              return (
                <a
                  key={f.id}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors group"
                  data-testid={`post-file-link-${f.id}`}
                >
                  <span className="text-lg">{emoji}</span>
                  <span className="flex-1 text-sm text-slate-700 truncate group-hover:text-church-teal">{f.original_filename}</span>
                  <span className="text-xs text-slate-400">Open</span>
                </a>
              );
            })}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-4 pt-3 border-t">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className={`gap-2 ${isLiked ? 'text-red-500' : 'text-slate-500'}`}
              onClick={() => onLike(post.id)}
              data-testid={`like-post-${post.id}`}
            >
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
              {post.likes?.length || 0}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-slate-500"
              onClick={() => setShowComments(!showComments)}
              data-testid={`toggle-comments-${post.id}`}
            >
              <MessageCircle className="h-4 w-4" />
              {post.comments?.length || 0}
            </Button>
          </div>
        </div>

        {showComments && (
          <div className="w-full space-y-4">
            {post.comments?.map((comment) => (
              <div key={comment.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={comment.author_image} alt={comment.author_name} />
                  <AvatarFallback className="bg-church-teal/10 text-church-teal text-sm">
                    {getInitials(comment.author_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{comment.author_name}</span>
                    <span className="text-xs text-muted-foreground">{timeAgo(comment.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{comment.content}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="min-h-[60px] resize-none"
                data-testid={`comment-input-${post.id}`}
              />
              <Button
                size="icon"
                className="h-[60px] w-12 bg-church-teal hover:bg-church-teal-dark rounded-xl"
                onClick={handleComment}
                disabled={submitting || !newComment.trim()}
                data-testid={`submit-comment-${post.id}`}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

const CreatePostDialog = ({ onPostCreated }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [files, setFiles] = useState([]); // uploaded file docs
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error('File must be under 30MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/posts/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiles((prev) => [...prev, r.data]);
      toast.success('Attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const handleSubmit = async () => {
    if (!content.trim() && files.length === 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/posts`, { content, category, file_ids: files.map((f) => f.id) });
      toast.success('Post shared with the community!');
      setContent('');
      setCategory('general');
      setFiles([]);
      setOpen(false);
      onPostCreated();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2 rounded-full bg-church-teal hover:bg-church-teal-dark" data-testid="create-post-btn">
          <Plus className="h-4 w-4" />
          New Post
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Share with the Community</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="post-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="testimony">Testimony</SelectItem>
                <SelectItem value="prayer">Prayer</SelectItem>
                <SelectItem value="sermon_notes">Sermon Notes</SelectItem>
                <SelectItem value="social">Social</SelectItem>
                <SelectItem value="announcement">Announcement</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={category === 'prayer' ? 'What would you like the community to pray for?' : category === 'sermon_notes' ? 'Sermon reflections, key verses, take-aways...' : "What's on your heart today?"}
            className="min-h-[150px]"
            data-testid="post-content-textarea"
          />
          {/* Attachments */}
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-church-teal">
              <input type="file" accept=".pdf,.pptx,.ppt,.docx,.doc,image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="post-file-input" />
              {uploading ? (
                <><Upload className="w-4 h-4 animate-pulse" /> Uploading...</>
              ) : (
                <><Paperclip className="w-4 h-4" /> Attach file (PDF, PPTX, DOCX, image)</>
              )}
            </label>
            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-xs bg-slate-100 rounded-lg px-2 py-1" data-testid={`post-attachment-${f.id}`}>
                    <FileText className="w-3.5 h-3.5 text-slate-500" />
                    <span className="flex-1 truncate">{f.original_filename}</span>
                    <button type="button" onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-500">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || (!content.trim() && files.length === 0)}
              className="rounded-full bg-church-teal hover:bg-church-teal-dark"
              data-testid="submit-post-btn"
            >
              {loading ? 'Posting...' : 'Share Post'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CheckInBanner = () => {
  const [checkedIn, setCheckedIn] = useState(false);
  const [todayCount, setTodayCount] = useState(0);
  const [todayNames, setTodayNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const [statusRes, todayRes] = await Promise.all([
        axios.get(`${API}/checkin/status`),
        axios.get(`${API}/checkin/today`),
      ]);
      setCheckedIn(statusRes.data.checked_in);
      setTodayCount(todayRes.data.count);
      setTodayNames(todayRes.data.checkins?.map(c => c.user_name) || []);
    } catch (error) {
      console.error('Failed to fetch check-in status');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    setChecking(true);
    try {
      const response = await axios.post(`${API}/checkin`, {});
      if (response.data.already_checked_in) {
        toast.info("You've already checked in today!");
      } else {
        toast.success("You're checked in! Welcome to church today.");
      }
      setCheckedIn(true);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to check in');
    } finally {
      setChecking(false);
    }
  };

  if (loading) return null;

  return (
    <Card className={cn(
      "mb-6 overflow-hidden transition-all",
      checkedIn
        ? "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50"
        : "border-church-teal/30 bg-gradient-to-r from-church-teal/5 to-teal-50"
    )} data-testid="checkin-banner">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-11 h-11 rounded-full flex items-center justify-center shrink-0",
              checkedIn ? "bg-green-500" : "bg-church-teal"
            )}>
              {checkedIn ? (
                <CheckCircle2 className="h-6 w-6 text-white" />
              ) : (
                <MapPin className="h-6 w-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">
                {checkedIn ? "You're checked in!" : "At church today?"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {checkedIn
                  ? "Thanks for being here. God bless your day!"
                  : "Tap to check in and let everyone know you're here"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {todayCount > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span className="font-medium">{todayCount}</span>
                <span className="hidden sm:inline">here today</span>
              </div>
            )}
            {!checkedIn && (
              <Button
                onClick={handleCheckin}
                disabled={checking}
                className="gap-2 bg-church-teal hover:bg-church-teal-dark rounded-full flex-1 sm:flex-initial"
                data-testid="checkin-btn"
              >
                <MapPin className="h-4 w-4" />
                {checking ? 'Checking in...' : "I'm Here!"}
              </Button>
            )}
          </div>
        </div>
        {todayCount > 0 && todayNames.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {todayNames.slice(0, 8).map((name, i) => (
              <Badge key={i} variant="secondary" className={cn(
                "text-xs",
                checkedIn ? "bg-green-100 text-green-700" : "bg-church-teal/10 text-church-teal"
              )}>
                {name}
              </Badge>
            ))}
            {todayNames.length > 8 && (
              <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-500">
                +{todayNames.length - 8} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchPosts = async () => {
    try {
      const params = filter !== 'all' ? { category: filter } : {};
      const response = await axios.get(`${API}/posts`, { params });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`);
      setPosts(posts.map(post => 
        post.id === postId ? { ...post, likes: response.data.likes } : post
      ));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleComment = async (postId, content) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, { content });
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, comments: [...(post.comments || []), response.data] } 
          : post
      ));
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/posts/${postId}`);
      setPosts(posts.filter(post => post.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  return (
    <Layout>
      <div className="container-app py-8" data-testid="feed-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-medium text-slate-800">Community Feed</h1>
            <p className="text-muted-foreground mt-1">Share and connect with our church family</p>
          </div>
          <CreatePostDialog onPostCreated={fetchPosts} />
        </div>
        <SpotifyLinkCard
          href={SPOTIFY_BIBLE_TALKS}
          title="ABC Bible Talks"
          subtitle="Catch up on recent sermons and Bible talks"
          testId="spotify-bible-talks-link"
          className="mb-8"
        />

        <CheckInBanner />

        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'general', 'prayer', 'testimony', 'sermon_notes', 'social', 'announcement'].map((cat) => (
            <Button
              key={cat}
              variant={filter === cat ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full ${filter === cat ? 'bg-church-teal hover:bg-church-teal-dark' : ''}`}
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat}`}
            >
              {cat === 'all' ? 'All' : (categoryLabels[cat] || cat)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-teal"></div>
          </div>
        ) : posts.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-muted-foreground">No posts yet. Be the first to share!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, index) => (
              <div key={post.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                <PostCard
                  post={post}
                  currentUserId={user?.id}
                  onLike={handleLike}
                  onComment={handleComment}
                  onDelete={handleDelete}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Feed;
